import React from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { appConfig } from 'brand-config';
import { useFormikContext, getIn } from 'formik';

import Field from 'core/components/form/form.v2';
import { AppliedTag } from 'common/components/farm/applied-tag';
import { SubRiskCollapsibleSection } from './sub-risk-collapsible-section';
import useStyles from './sub-risk.styles';

const UPP_COVERAGE_ITEMS = [
  'moneyLimit',
  'jewelryLimit',
  'securitiesLimit',
  'motorizedVehiclesLimit',
  'silverwareLimit',
  'gunsLimit',
  'businessPropertyLimit'
];

const UnscheduledPersonalPropertyForm = ({ fieldName, disabled, state, riskType }) => {
  const classes = useStyles();
  const { values } = useFormikContext();

  // State-level flag to hide UPP entirely for a brand (e.g., Forreston — GitHub issue #28933)
  if (appConfig.farm?.[state]?.featureFlags?.hideUnscheduledPersonalProperty) {
    return null;
  }

  // Per-item hidden coverages check (fallback for brands that only hide specific items)
  const hiddenCoverages = appConfig.farm?.[state]?.risks?.[riskType]?.hiddenCoverages || [];
  const shouldHideUNPP = UPP_COVERAGE_ITEMS.every((item) => hiddenCoverages.includes(item));

  if (shouldHideUNPP) {
    return null;
  }

  // Auto-expand if any UPP limits have been set (non-zero values)
  const hasConfiguredLimits = UPP_COVERAGE_ITEMS.some((item) => {
    const value = getIn(values, `${fieldName}.${item}`);
    return value && value > 0;
  });

  return (
    <SubRiskCollapsibleSection
      type="secondary"
      content={<div className={classes.headerText}>Unscheduled personal property</div>}
      contentRight={hasConfiguredLimits ? <AppliedTag /> : null}
      initialOpen={hasConfiguredLimits}
    >
      <Grid container spacing={2}>
        <Field
          xs={4}
          type="select"
          id={`${fieldName}.moneyLimit`}
          name={`${fieldName}.moneyLimit`}
          mode="dark"
          label="Money"
          disabled={disabled}
          options={[
            { id: 0, value: 'None' },
            { id: 500, value: '$500' },
            { id: 1500, value: '$1,500' },
            { id: 2000, value: '$2,000' },
            { id: 5000, value: '$5,000' }
          ]}
        />
        <Field
          xs={4}
          type="select"
          id={`${fieldName}.securitiesLimit`}
          name={`${fieldName}.securitiesLimit`}
          mode="dark"
          label="Securities"
          disabled={disabled}
          options={[
            { id: 0, value: 'None' },
            { id: 500, value: '$500' },
            { id: 1500, value: '$1,500' },
            { id: 2000, value: '$2,000' },
            { id: 5000, value: '$5,000' }
          ]}
        />
        <Field
          xs={4}
          type="select"
          id={`${fieldName}.motorizedVehiclesLimit`}
          name={`${fieldName}.motorizedVehiclesLimit`}
          mode="dark"
          label="Motorized vehicles"
          disabled={disabled}
          options={[
            { id: 0, value: 'None' },
            { id: 500, value: '$500' },
            { id: 1500, value: '$1,500' },
            { id: 2000, value: '$2,000' },
            { id: 5000, value: '$5,000' }
          ]}
        />
        <Field
          xs={4}
          type="select"
          id={`${fieldName}.jewelryLimit`}
          name={`${fieldName}.jewelryLimit`}
          mode="dark"
          label="Jewelry, watches, and furs"
          disabled={disabled}
          options={[
            { id: 0, value: 'None' },
            { id: 500, value: '$500' },
            { id: 1500, value: '$1,500' },
            { id: 2000, value: '$2,000' },
            { id: 5000, value: '$5,000' }
          ]}
        />
        <Field
          xs={4}
          type="select"
          id={`${fieldName}.silverwareLimit`}
          name={`${fieldName}.silverwareLimit`}
          mode="dark"
          label="Silverware"
          disabled={disabled}
          options={[
            { id: 0, value: 'None' },
            { id: 500, value: '$500' },
            { id: 1500, value: '$1,500' },
            { id: 2000, value: '$2,000' },
            { id: 5000, value: '$5,000' }
          ]}
        />
        <Field
          xs={4}
          type="select"
          id={`${fieldName}.gunsLimit`}
          name={`${fieldName}.gunsLimit`}
          mode="dark"
          label="Guns"
          disabled={disabled}
          options={[
            { id: 0, value: 'None' },
            { id: 500, value: '$500' },
            { id: 1500, value: '$1,500' },
            { id: 2000, value: '$2,000' },
            { id: 5000, value: '$5,000' }
          ]}
        />
        <Field
          xs={4}
          type="select"
          id={`${fieldName}.businessPropertyLimit`}
          name={`${fieldName}.businessPropertyLimit`}
          mode="dark"
          label="Business property"
          disabled={disabled}
          options={[
            { id: 0, value: 'None' },
            { id: 500, value: '$500' },
            { id: 1500, value: '$1,500' },
            { id: 2000, value: '$2,000' },
            { id: 5000, value: '$5,000' }
          ]}
        />
      </Grid>
    </SubRiskCollapsibleSection>
  );
};

UnscheduledPersonalPropertyForm.propTypes = {
  fieldName: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  state: PropTypes.string,
  riskType: PropTypes.string
};

UnscheduledPersonalPropertyForm.defaultProps = {
  disabled: false
};

export default observer(UnscheduledPersonalPropertyForm);
